import type {ReactNode} from 'react';
import {useLayoutEffect, useRef} from 'react';

import {typedMemo} from '#utils/react.js';

import {Item, List} from '../components/list.js';
import {useMultiComboBoxContext} from '../context.js';
import {appendUniqueByKey} from '../utils.js';

type ItemsProps<T> = {
  children?: (props: {
    item: T;
    index: number;
    isSelected: boolean;
    isDisabled: boolean;
  }) => ReactNode;
  isItemDisabled?: (item: T) => boolean;
};

const Items = typedMemo(function Items<T>({
  children,
  isItemDisabled: isItemDisabledProp,
}: ItemsProps<T>) {
  const {
    getMenuProps,
    getItemProps,
    filteredItems,
    customItemsList,
    selectedItems,
    itemToString,
    itemToKey,
    setItemsList,
    loadItems,
    inputValue,
    isOpen,
    isLoading,
    hasMore,
    setHasMore,
    itemsListRef,
    infiniteLoaderRef,
    hasExternalInfiniteLoader,
    isItemDisabled: isItemDisabledContext,
    emptyStateMessage,
  } = useMultiComboBoxContext<T>();
  // Prefer prop over context for backward compatibility during migration
  const isItemDisabled = isItemDisabledProp ?? isItemDisabledContext;
  const customLength = customItemsList.length;
  const ref = useRef<HTMLUListElement>(null);
  const queryVersionRef = useRef(0);
  const itemToKeyRef = useRef(itemToKey);
  itemToKeyRef.current = itemToKey;
  const hasInfiniteLoader = Boolean(
    loadItems || (hasExternalInfiniteLoader && infiniteLoaderRef.current)
  );
  const shouldShowEmptyState =
    isOpen &&
    !isLoading &&
    customItemsList.length === 0 &&
    filteredItems.length === 0;

  useLayoutEffect(() => {
    if (hasInfiniteLoader) {
      queryVersionRef.current += 1;
    }
  }, [inputValue, hasInfiniteLoader]);

  useLayoutEffect(() => {
    if (ref.current && isOpen && hasInfiniteLoader && !isLoading) {
      const root = ref.current;
      const target = root.lastElementChild;
      const rootMargin = target
        ? `${target.clientHeight}px 0px 0px 0px`
        : '0px';
      const observer = new IntersectionObserver(
        async entries => {
          for (const entry of entries) {
            if (entry.isIntersecting && hasMore) {
              if (loadItems) {
                const requestVersion = queryVersionRef.current;
                const {items, hasMore: nextHasMore} =
                  await loadItems(inputValue);

                if (requestVersion !== queryVersionRef.current) {
                  return;
                }

                const {items: nextItems, appendedCount} = appendUniqueByKey(
                  itemsListRef.current,
                  items,
                  itemToKeyRef.current
                );
                if (appendedCount > 0) {
                  setItemsList(nextItems);
                }
                setHasMore(appendedCount > 0 && nextHasMore);
              } else if (infiniteLoaderRef.current) {
                const requestVersion = queryVersionRef.current;
                try {
                  const nextHasMore = await infiniteLoaderRef.current();
                  if (requestVersion !== queryVersionRef.current) {
                    return;
                  }

                  if (typeof nextHasMore === 'boolean') {
                    setHasMore(nextHasMore);
                  }
                } catch (error) {
                  if (requestVersion === queryVersionRef.current) {
                    setHasMore(false);
                  }
                  throw error;
                }
              }
            }
          }
        },
        {
          root,
          rootMargin,
          threshold: 0,
        }
      );

      if (target) {
        observer.observe(target);
      }

      return () => {
        observer.disconnect();
      };
    }
  }, [
    ref,
    isOpen,
    // Create a new observer after filtering items
    inputValue,
    filteredItems,
    hasMore,
    isLoading,
    setHasMore,
    setItemsList,
    hasInfiniteLoader,
    loadItems,
    itemsListRef,
    infiniteLoaderRef,
    hasExternalInfiniteLoader,
  ]);

  return (
    <List {...getMenuProps({ref})}>
      {isOpen &&
        customItemsList.map((item, index) => (
          <Item
            isLastCustom={index === customItemsList.length - 1}
            isSelected={selectedItems.some(
              selectedItem => itemToKey(selectedItem) === itemToKey(item)
            )}
            key={`${itemToKey(item)}-${index}`}
            {...getItemProps({
              item,
              index,
            })}
          >
            {itemToString(item)}
          </Item>
        ))}
      {isOpen &&
        filteredItems.map((item, index) => {
          const isSelected = selectedItems.some(
            selectedItem => itemToKey(selectedItem) === itemToKey(item)
          );
          const isDisabled = isItemDisabled?.(item) ?? false;

          return (
            <Item
              isSelected={isSelected}
              isDisabled={isDisabled}
              key={`${itemToKey(item)}-${index}`}
              {...getItemProps({
                item,
                index: customLength + index,
              })}
            >
              {children
                ? children({item, index, isSelected, isDisabled})
                : itemToString(item)}
            </Item>
          );
        })}
      {shouldShowEmptyState && <li aria-live="polite">{emptyStateMessage}</li>}
    </List>
  );
});

export type {ItemsProps};
export {Items};
